import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Input } from '../common';
import { useProductionStore } from '../../stores/productionStore';
import { validateNumeric, validatePositive } from '../../lib/validation/validators';
import { useUnitStore } from '../../stores/unitStore';

export function ProductionDataForm() {
  const { t } = useTranslation();
  const { weightUnit } = useUnitStore();
  const { data, updateProductionData } = useProductionStore();
  const [manualMode, setManualMode] = useState(true); // Default: Kesim yaşı ile hesapla

  const handlePlacementDateChange = (value: string) => {
    updateProductionData({ placementDate: value });
    
    // Smart calculation: if harvestDate exists, calculate cycleDaysManual
    if (data.harvestDate && value) {
      const days = calculateDaysBetween(value, data.harvestDate);
      if (days >= 0) {
        updateProductionData({ cycleDaysManual: days });
      }
    }
  };

  const handleHarvestDateChange = (value: string) => {
    updateProductionData({ harvestDate: value });
    
    // Smart calculation: if placementDate exists, calculate cycleDaysManual
    if (data.placementDate && value) {
      const days = calculateDaysBetween(data.placementDate, value);
      if (days >= 0) {
        updateProductionData({ cycleDaysManual: days });
      }
    }
  };

  const handleCycleDaysChange = (value: string) => {
    // Boş string'e izin ver (kullanıcı silme yapıyor olabilir)
    if (value === '') {
      updateProductionData({ cycleDaysManual: '' as any });
      return;
    }

    const numValue = parseInt(value);
    
    if (isNaN(numValue) || numValue < 0) {
      return;
    }

    updateProductionData({ cycleDaysManual: numValue });
    
    // Smart calculation: if placementDate exists, calculate harvestDate
    if (data.placementDate && numValue > 0) {
      const harvestDate = addDaysToDate(data.placementDate, numValue);
      updateProductionData({ harvestDate });
    }
  };

  const calculateDaysBetween = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const addDaysToDate = (dateStr: string, days: number): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const handleNumberChange = (field: keyof typeof data, value: string) => {
    // Boş string'e izin ver (kullanıcı silme yapıyor olabilir)
    if (value === '') {
      updateProductionData({ [field]: '' as any });
      return;
    }

    // Sayı parse edilebiliyorsa kaydet (validasyon blur'da yapılacak)
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateProductionData({ [field]: numValue });
    }
  };

  // Validation errors
  const getFieldError = (field: keyof typeof data, value: number): string | undefined => {
    // feedRemaining için 0 değeri kabul edilir
    if (field === 'feedRemaining' && value === 0) {
      return undefined;
    }
    
    // Diğer alanlar için 0 değeri kabul edilmez
    if (value === 0 && field !== 'feedRemaining') {
      return t('productionData.positiveNumber');
    }
    
    if (!validateNumeric(value)) {
      return t('productionData.validNumber');
    }
    
    if (field !== 'feedRemaining' && !validatePositive(value)) {
      return t('productionData.positiveNumber');
    }
    
    return undefined;
  };

  return (
    <Card title={t('productionData.title')}>
      {/* Mode Toggle Button */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setManualMode(!manualMode)}
          className="px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg active:scale-95"
        >
          {manualMode ? (
            <>
              <span>📅</span>
              <span>{t('productionData.knowDates')}</span>
            </>
          ) : (
            <>
              <span>⏱️</span>
              <span>{t('productionData.calculateByAge')}</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {manualMode ? (
          <>
            {/* Manual Mode: Only Cycle Days */}
            <Input
              label={t('productionData.harvestAge')}
              type="number"
              min="0"
              step="1"
              value={data.cycleDaysManual ?? ''}
              onChange={(e) => handleCycleDaysChange(e.target.value)}
              required
              helperText={t('productionData.harvestAgeHelper')}
            />
          </>
        ) : (
          <>
            {/* Date Mode: Only Dates, cycle days calculated automatically */}
            <Input
              label={t('productionData.placementDate')}
              type="date"
              value={data.placementDate}
              onChange={(e) => handlePlacementDateChange(e.target.value)}
              required
              helperText={t('productionData.placementDateHelper')}
            />

            <Input
              label={t('productionData.harvestDate')}
              type="date"
              value={data.harvestDate}
              onChange={(e) => handleHarvestDateChange(e.target.value)}
              required
              helperText={t('productionData.harvestDateHelper')}
            />
          </>
        )}

        {/* Chick Count */}
        <Input
          label={t('productionData.chickCount')}
          type="number"
          min="0"
          step="1"
          value={data.chickCount ?? ''}
          onChange={(e) => handleNumberChange('chickCount', e.target.value)}
          error={getFieldError('chickCount', data.chickCount)}
          required
          helperText={t('productionData.chickCountHelper')}
        />

        {/* Feed Incoming */}
        <Input
          label={t('productionData.feedIncoming', { unit: weightUnit })}
          type="number"
          min="0"
          step="0.01"
          value={data.feedIncoming ?? ''}
          onChange={(e) => handleNumberChange('feedIncoming', e.target.value)}
          error={getFieldError('feedIncoming', data.feedIncoming)}
          required
          helperText={t('productionData.feedIncomingHelper')}
        />

        {/* Feed Remaining */}
        <Input
          label={t('productionData.feedRemaining', { unit: weightUnit })}
          type="number"
          min="0"
          step="0.01"
          value={data.feedRemaining ?? ''}
          onChange={(e) => handleNumberChange('feedRemaining', e.target.value)}
          error={getFieldError('feedRemaining', data.feedRemaining)}
          required
          helperText={t('productionData.feedRemainingHelper')}
        />

        {/* Feed Consumed (Read-only, calculated) */}
        <div className="md:col-span-2">
          <Input
            label={t('productionData.feedConsumed', { unit: weightUnit })}
            type="number"
            value={data.feedConsumed.toFixed(2)}
            readOnly
            helperText={t('productionData.feedConsumedHelper')}
            className="bg-gray-50 dark:bg-gray-800"
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {manualMode ? (
                t('productionData.manualModeInfo')
              ) : (
                t('productionData.smartCalculationInfo')
              )}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
